/**
 * Finding where a diagram item is mentioned in its written explanation.
 *
 * The explanation is Markdown whose links look like `[booking](#table:booking)`. An item "has a reference" when
 * some link points at it. Each item describes what to look for as an ordered list of steps: the first step that
 * matches somewhere wins, so a table's own section beats a passing mention of one of its fields.
 */

export type Matcher = (kind: string, target: string) => boolean;
/** `any`: some reference matches. `both`: one line / table row / paragraph contains references matching both. */
export type Step = { any: Matcher } | { both: [Matcher, Matcher] };

export interface ParsedRef {
  line: number;
  kind: string;
  target: string;
}

const REF = /\]\(#([a-z]+):([^\s)]*)\)/g;

export function parseReferences(markdown: string): ParsedRef[] {
  const refs: ParsedRef[] = [];
  markdown.split('\n').forEach((text, line) => {
    for (const m of text.matchAll(REF)) {
      if (m[1] === 'section') continue;
      let target = m[2];
      try {
        target = decodeURIComponent(target);
      } catch {
        // keep the raw text
      }
      refs.push({ line, kind: m[1], target });
    }
  });
  return refs;
}

/** True when the explanation mentions the item described by `steps`. */
export function hasReference(markdown: string, steps: Step[]): boolean {
  if (!markdown.trim()) return false;
  const refs = parseReferences(markdown);
  return steps.some((step) => {
    if ('any' in step) return refs.some((r) => step.any(r.kind, r.target));
    const [a, b] = step.both;
    const lines = new Set(refs.filter((r) => a(r.kind, r.target)).map((r) => r.line));
    return refs.some((r) => b(r.kind, r.target) && lines.has(r.line));
  });
}

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

export const tableRef = (name: string): Matcher => (k, t) => k === 'table' && same(t, name);
export const columnRef = (table: string, column: string): Matcher => (k, t) => k === 'column' && same(t, `${table}.${column}`);
export const columnsOfTable = (table: string): Matcher => (k, t) => k === 'column' && same(t.split('.')[0], table);
export const groupRef = (name: string): Matcher => (k, t) => k === 'group' && same(t, name);

/** The same label lookup the explanation links use: exact label, then one that starts with the text, then one that contains it. */
export function resolveNodeByLabel<T extends { label: string }>(nodes: T[], target: string): T | undefined {
  const wanted = target.toLowerCase();
  return (
    nodes.find((n) => n.label.toLowerCase() === wanted) ??
    nodes.find((n) => n.label.toLowerCase().startsWith(wanted)) ??
    nodes.find((n) => n.label.toLowerCase().includes(wanted))
  );
}
export const nodeRef = <T extends { id: string; label: string }>(nodes: T[], node: { id: string }): Matcher => (k, t) =>
  k === 'node' && resolveNodeByLabel(nodes, t)?.id === node.id;

// ---- steps for each kind of diagram item
export const tableSteps = (table: string): Step[] => [{ any: tableRef(table) }, { any: columnsOfTable(table) }];
export const fieldSteps = (table: string, column: string): Step[] => [{ any: columnRef(table, column) }, { any: tableRef(table) }];
export const groupSteps = (group: string): Step[] => [{ any: groupRef(group) }];
export const relationshipSteps = (from: { table: string; column: string }, to: { table: string; column: string }): Step[] => [
  { both: [columnRef(from.table, from.column), columnRef(to.table, to.column)] },
  { any: columnRef(from.table, from.column) },
  { any: columnRef(to.table, to.column) },
  { any: tableRef(from.table) },
  { any: tableRef(to.table) },
];
export const nodeSteps = (matcher: Matcher): Step[] => [{ any: matcher }];
export const connectorSteps = (from: Matcher, to: Matcher): Step[] => [{ both: [from, to] }, { any: from }, { any: to }];

// ---- finding the link inside the rendered explanation

const BLOCK = 'li, tr, p, blockquote, [role="heading"]';

/** The best rendered reference link for `steps`, preferring one inside a heading (the item's own section). */
export function findReferenceElement(root: HTMLElement, steps: Step[]): HTMLElement | null {
  const anchors = [...root.querySelectorAll<HTMLElement>('a[data-ref-kind]')].map((el) => ({
    el,
    kind: el.dataset.refKind ?? '',
    target: el.dataset.refTarget ?? '',
  }));
  const inHeading = (el: HTMLElement) => !!el.closest('[role="heading"]');

  // How deep is the section an element sits in? A mention inside a table's own "###" entry describes that table far
  // better than a passing mention in the "##" overview, so the deeper section wins.
  const headings = [...root.querySelectorAll<HTMLElement>('[role="heading"]')];
  const sectionLevel = (el: HTMLElement) => {
    let level = 0;
    for (const h of headings) {
      if (h.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) level = Number(h.getAttribute('aria-level')) || 0;
      else break;
    }
    return level;
  };

  for (const step of steps) {
    if ('any' in step) {
      const hits = anchors.filter((a) => step.any(a.kind, a.target));
      if (hits.length) {
        const own = hits.find((a) => inHeading(a.el));
        if (own) return own.el;
        // deepest section first; ties keep document order
        return hits.map((a, i) => ({ a, i, level: sectionLevel(a.el) })).sort((x, y) => y.level - x.level || x.i - y.i)[0].a.el;
      }
    } else {
      const [a, b] = step.both;
      for (const first of anchors.filter((x) => a(x.kind, x.target))) {
        const block = first.el.closest(BLOCK);
        if (block && anchors.some((y) => b(y.kind, y.target) && block.contains(y.el))) return first.el;
      }
    }
  }
  return null;
}
